import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { dashboardAPI } from '../utils/api';
import { tField } from '../utils/bilingualHelper';
import { useTranslation } from 'react-i18next';

function StatCard({ icon, value, label, color }) {
  return (
    <div className="dash-stat-card">
      <div className="dash-stat-icon" style={{ background: color + '22', color }}>{icon}</div>
      <div>
        <div className="dash-stat-val" style={{ color }}>{value?.toLocaleString() ?? '—'}</div>
        <div className="dash-stat-label">{label}</div>
      </div>
    </div>
  );
}

const getNormalizedStatus = (status) => {
  if (!status) return 'New';
  const s = status.toString().trim().toLowerCase();
  if (s === 'new') return 'New';
  if (s === 'contacted') return 'Contacted';
  if (s === 'in progress' || s === 'inprogress') return 'In Progress';
  if (s === 'enrolled') return 'Enrolled';
  if (s === 'resolved') return 'Resolved';
  if (s === 'rejected') return 'Rejected';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const STATUS_BADGE = { 
  New: 'badge-blue', 
  Contacted: 'badge-orange', 
  'In Progress': 'badge-orange', 
  Enrolled: 'badge-green', 
  Resolved: 'badge-green', 
  Rejected: 'badge-red' 
};

export default function Overview() {
  const { t } = useTranslation();
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const navigate            = useNavigate();

  useEffect(() => {
    let isMounted = true;
    dashboardAPI.getOverview()
      .then(r => {
        if (isMounted) setData(r.data);
      })
      .catch(err => {
        if (isMounted) console.error(err);
      })
      .finally(() => {
        if (isMounted) setLoad(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const stats = data?.stats || {};

  return (
    <Layout title={t('overview.title')}>
      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">{t('overview.title')}</h1>
          <p className="text-sm text-muted" style={{ marginTop:4 }}>{t('overview.subtitle')}</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/batches')}>{t('overview.newClass')}</button>
      </div>

      {loading ? (
        <div className="dash-loading">⏳ {t('overview.loading')}</div>
      ) : (
        <>
          {/* Stats */}
          <div className="dash-stats-grid mb-8">
            <StatCard icon="👥" value={stats.students} label={t('overview.totalStudents')}    color="#3b7dd8" />
            <StatCard icon="💰" value={`HK$ ${stats.monthlyRevenue?.toLocaleString()}`} label={t('overview.monthlyRev')} color="#2d7a52" />
            <StatCard icon="📊" value={`HK$ ${stats.yearlyRevenue?.toLocaleString()}`} label={t('overview.yearlyRev')} color="#c04060" />
            <StatCard icon="💬" value={stats.enquiries}label={t('overview.newEnquiries')}     color="#e07d2a" />
          </div>

          <div className="grid-2 mb-6" style={{ gap:24 }}>
            {/* Recent Enquiries */}
            <div className="dash-card">
              <div className="dash-card-header">
                <div className="dash-card-title">{t('overview.recentEnquiries')}</div>
                <button className="btn btn-sm btn-outline" onClick={() => navigate('/enquiries')}>{t('overview.viewAll')}</button>
              </div>
              {!data?.recentEnquiries?.length ? (
                <div className="dash-empty" style={{ padding:'40px 20px' }}>
                  <div className="dash-empty-icon">💬</div>
                  <div className="dash-empty-title">{t('overview.noEnquiries')}</div>
                </div>
              ) : (
                data.recentEnquiries.map((enq, index) => (
                  <div key={enq._id || enq.id || index} className="dash-list-item" onClick={() => navigate('/enquiries')}>
                    <div className="dash-avatar">👤</div>
                    <div className="dash-list-item-main">
                      <div className="dash-list-item-name">{t('overview.enquiry')} {enq.enquiryNumber}</div>
                      <div className="dash-list-item-sub">{enq.mobile || enq.email}</div>
                    </div>
                    <span className={`badge ${STATUS_BADGE[getNormalizedStatus(enq.status)] || 'badge-blue'}`}>{getNormalizedStatus(enq.status)}</span>
                  </div>
                ))
              )}
            </div>

            {/* Recent Payments */}
            <div className="dash-card">
              <div className="dash-card-header">
                <div className="dash-card-title">{t('overview.recentPayments')}</div>
                <button className="btn btn-sm btn-outline" onClick={() => navigate('/payments')}>{t('overview.auditor')}</button>
              </div>
              {!data?.recentPayments?.length ? (
                <div className="dash-empty" style={{ padding:'40px 20px' }}>
                  <div className="dash-empty-icon">💳</div>
                  <div className="dash-empty-title">{t('overview.noPayments')}</div>
                </div>
              ) : (
                data.recentPayments.map((p, index) => (
                  <div key={p._id || index} className="dash-list-item" onClick={() => navigate('/payments')}>
                    <div className="dash-avatar" style={{ background:'var(--wine-pale)', color:'var(--wine)' }}>💰</div>
                    <div className="dash-list-item-main">
                      <div className="dash-list-item-name">
                        {tField(p.student?.name) || p.guardianName || 'Unknown Student'}
                        {p.studentId && <span style={{ color: '#888', fontWeight: 'normal', fontSize: '0.9em', marginLeft: '6px' }}>#{p.studentId}</span>}
                      </div>
                      <div className="dash-list-item-sub">HK$ {p.amount} • {p.month}</div>
                    </div>
                    <span className={`badge ${p.verificationStatus === 'Approved' ? 'badge-green' : 'badge-orange'}`}>{p.verificationStatus}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Quick links */}
          <div className="grid-3">
            <div className="dash-card" style={{ cursor:'pointer' }} onClick={() => navigate('/faculty')}>
              <div style={{ fontSize:32, marginBottom:12 }}>🎓</div>
              <div className="dash-card-title">{t('overview.facultyRoom')}</div>
              <p className="text-xs text-muted">{t('overview.facultyDesc')}</p>
            </div>
            <div className="dash-card" style={{ cursor:'pointer' }} onClick={() => navigate('/students')}>
              <div style={{ fontSize:32, marginBottom:12 }}>👥</div>
              <div className="dash-card-title">{t('overview.studentHub')}</div>
              <p className="text-xs text-muted">{t('overview.studentDesc')}</p>
            </div>
            <div className="dash-card" style={{ cursor:'pointer' }} onClick={() => navigate('/library')}>
              <div style={{ fontSize:32, marginBottom:12 }}>📚</div>
              <div className="dash-card-title">{t('overview.videoLib')}</div>
              <p className="text-xs text-muted">{t('overview.libDesc')}</p>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
